import * as THREE from "three";
import { AttachmentPoint, MARKER_SIZE } from "../furniture/AttachmentPoint.js";
import { isPointInsideFurnitureXZ } from "../utils/geometry.js";
import { freeRun } from "./interaction/componentDrag.js";

// Distance threshold for snapping components (panels/cushions) to an AP.
const SNAP_DISTANCE = 0.6;

// A cursor ray this close to an AP is over its plus (drawn over everything, so maybe behind the surface hit).
const PLUS_PICK_RADIUS_SQ = (MARKER_SIZE / 2) ** 2;

// Distance threshold for module-to-module connector snapping, measured as the
// landing displacement of the dragged piece. Unity uses
// MaxSnapDistanceWhileDragging = 1.0 (BaseUISystem.cs); we run tighter so the
// snap only engages once the piece is genuinely near its drop pose. Raising it
// makes snapping grabbier; lowering it further leaves dead zones between a
// module's faces, so the target won't sweep face-to-face as the cursor orbits.
export const MODULE_SNAP_DISTANCE = 0.5;

// Minimum required face overlap (metres) to allow a snap connection.
// Prevents corner-to-corner diagonal snaps where modules only touch at a point.
const MIN_FACE_OVERLAP = 0.01;

// Near-tie window (metres) for the winner selection. When two candidate faces have
// landing distances within this margin, prefer the less-rotated one so the choice
// doesn't jitter right at the boundary between two faces.
const SELECTION_DIST_EPSILON = 0.02;
// How close (rad) a child anchor's fit rotation must be to a requiredRotation.
const ROTATION_MATCH_TOLERANCE = 0.01;

/**
 * SnapSystem — owns all module-to-module snap detection and position calculation.
 *
 * Extracted from EventManager to separate concerns. EventManager delegates
 * every snap query to this class via `this.snapSystem`.
 */
export class SnapSystem {
  constructor(app) {
    this.app = app;
    this._scratchVec = new THREE.Vector3();
    // The ranged-AP run whose plus the last findClosestComponentAttachment ray crossed: {ap, t}.
    this.pickedRun = null;
  }

  /**
   * Whether a raw child AP config (the dragged piece) can mate with a scene AttachmentPoint.
   * The Unity-parity anchor/tag rule lives in AttachmentPoint.canMatch; this is its child-first call.
   */
  _canMatch(childAPData, parentAP) {
    return AttachmentPoint.canMatch(childAPData, parentAP);
  }

  // Scratch objects — allocated once, reused on every call
  _parentWorldQuat = new THREE.Quaternion();
  _rotationQuat = new THREE.Quaternion();
  _parentLocalNormal = new THREE.Vector3();
  _candidateOffset = new THREE.Vector3();
  _candidatePos = new THREE.Vector3();
  _alongSideAxisLocal = new THREE.Vector3();
  _worldAlongSideAxis = new THREE.Vector3();
  _parentNormalWorld = new THREE.Vector3();
  _parentAPWorld = new THREE.Vector3();
  _faceAPWorld = new THREE.Vector3();
  _tempVec = new THREE.Vector3();
  _yAxis = new THREE.Vector3(0, 1, 0);
  _runStart = new THREE.Vector3();
  _runEnd = new THREE.Vector3();
  _runLine = new THREE.Line3();

  /**
   * Find the closest unoccupied module attachment point within snap distance.
   *
   * Candidate faces are ranked by LANDING DISPLACEMENT — how far the dragged piece
   * would travel to snap onto each face (rotation included). The face whose snapped
   * pose is nearest the live drag position wins, so micro-moving the cursor toward a
   * different side of a module re-targets that side and rotates the piece to fit it.
   * This mirrors Unity's SpecialCaseSnapping ranking, generalised to all pieces.
   *
   * @param {THREE.Vector3} position - World position to check from
   * @param {import('../furniture/FurnitureElement.js').FurnitureElement|null} excludeFurniture
   * @param {Array} childAttachmentPoints - The dragged furniture's AP definitions (for tag filtering)
   * @param {number} currentRotation - Current Y rotation of the dragged piece (frozen drag-select rotation)
   * @param {boolean} [specialCaseSnapping] - Accepted for call-site compatibility; the
   *   nearest-face ranking now applies to every piece, so this no longer changes selection.
   * @returns {import('../furniture/AttachmentPoint.js').AttachmentPoint|null}
   */
  findClosestModuleAttachment(position, excludeFurniture = null, childAttachmentPoints = [], currentRotation = 0, specialCaseSnapping = false) {
    const allFurniture = this.app.getFurnitureManager()?.getAllFurniture() || [];
    const childModuleAPs = childAttachmentPoints.filter(ap => ap.accepts?.includes("module"));
    const candidates = [];

    allFurniture.forEach((furnitureElement) => {
      if (excludeFurniture && furnitureElement === excludeFurniture) return;
      if (excludeFurniture && furnitureElement.attachedTo?.parentFurniture === excludeFurniture) return;

      if (furnitureElement.attachmentPoints) {
        furnitureElement.attachmentPoints.forEach((attachmentPoint) => {
          if (!attachmentPoint.acceptsComponentTypes.includes("module") || attachmentPoint.isOccupied) return;

          if (this._isFaceBlocked(attachmentPoint, allFurniture, excludeFurniture)) return;

          // Check if ANY child module AP can match this parent AP
          const compatible = childModuleAPs.some(cap => this._canMatch(cap, attachmentPoint));
          if (!compatible) return;

          const snapResult = this.calculateSnapPosition(
            attachmentPoint,
            childAttachmentPoints,
            currentRotation,
            position
          );
          if (!snapResult) return;

          // Rank by LANDING DISPLACEMENT: how far the piece would travel from its live
          // drag pose to this candidate's snapped pose. snapResult.position already
          // bakes in the required fit rotation, so this is rotation-aware — a face that
          // needs the piece rotated competes fairly: when the cursor is nearest that
          // face its landing distance is smallest, so it wins and the piece rotates to
          // fit. Mirrors Unity's SpecialCaseSnapping metric
          // (FurnitureManager.cs:677 — SqrMagnitude(currentCenter - fitCenter)).
          //
          // The old connector-distance metric measured the child anchor at the CURRENT
          // (un-rotated) pose, so a face requiring rotation always scored a large
          // distance and could never win — which is why micro-moving the cursor to the
          // opposite side never flipped/rotated the piece onto the nearer face.
          const distance = position ? snapResult.position.distanceTo(position) : 0;
          if (distance >= MODULE_SNAP_DISTANCE) return;

          // Rotation delta from the current pose — kept only to break near-ties in
          // favour of the less-rotated face (stability).
          let rotDiff = snapResult.rotation - currentRotation;
          while (rotDiff >  Math.PI) rotDiff -= 2 * Math.PI;
          while (rotDiff < -Math.PI) rotDiff += 2 * Math.PI;
          const absRotDiff = Math.abs(rotDiff);

          candidates.push({ attachmentPoint, distance, absRotDiff });
        });
      }
    });

    // Winner = nearest landing across ALL faces, so the snap follows whichever face the
    // cursor is closest to and rotates the piece to fit. On near-ties (within
    // SELECTION_DIST_EPSILON) prefer the less-rotated face to keep the choice stable at
    // face boundaries. Every piece uses this nearest-face-wins selection (Unity's
    // special-case behaviour generalised) — specialCaseSnapping no longer needs its own
    // path, and there is no hysteresis: micro-moving the cursor continuously re-fits.
    candidates.sort((a, b) => {
      const d = a.distance - b.distance;
      if (Math.abs(d) > SELECTION_DIST_EPSILON) return d;
      return a.absRotDiff - b.absRotDiff;
    });

    return candidates[0]?.attachmentPoint ?? null;
  }

  // An AP whose face is already covered by a neighbouring module's body (the "yellow" APs in
  // the debug overlay) can't take a module, even while isOccupied is still false.
  _isFaceBlocked(attachmentPoint, allFurniture, excludeFurniture) {
    const apWorld = attachmentPoint.getWorldPosition(this._scratchVec);
    return allFurniture.some(other => {
      if (other === attachmentPoint.parentFurniture) return false;
      if (excludeFurniture && other === excludeFurniture) return false;
      if (other.furnitureData?.componentType !== "module") return false;
      return isPointInsideFurnitureXZ(other, apWorld.x, apWorld.z, 0.05);
    });
  }

  /**
   * Every snap of the child onto a free module AP of `parents` that keeps the child at exactly
   * `rotation`, nearest landing first. This is for re-attaching a piece turned in place. Drag
   * snapping lets the fit rotation follow the nearest face, which here would undo the turn, so
   * every face is considered and rotation is a constraint rather than a tiebreak.
   * @returns {Array<{attachmentPoint, snap, distance}>}
   */
  findSnapsAtRotation(parents, excludeFurniture, childAttachmentPoints, rotation, position) {
    const allFurniture = this.app.getFurnitureManager()?.getAllFurniture() || [];
    const childModuleAPs = childAttachmentPoints.filter(ap => ap.accepts?.includes("module"));
    const results = [];
    for (const parent of parents) {
      if (parent === excludeFurniture) continue;
      for (const attachmentPoint of parent.attachmentPoints ?? []) {
        if (!attachmentPoint.acceptsComponentTypes.includes("module") || attachmentPoint.isOccupied) continue;
        if (!childModuleAPs.some(cap => this._canMatch(cap, attachmentPoint))) continue;
        if (this._isFaceBlocked(attachmentPoint, allFurniture, excludeFurniture)) continue;
        const snap = this.calculateSnapPosition(attachmentPoint, childAttachmentPoints, rotation, position, rotation);
        if (snap) results.push({ attachmentPoint, snap, distance: snap.position.distanceTo(position) });
      }
    }
    return results.sort((a, b) => a.distance - b.distance);
  }

  /** Free AP for a dragged component: the one whose plus the cursor `ray` crosses, else the nearest to `position`. */
  findClosestComponentAttachment(position, furnitureData, excludeFurniture = null, ray = null) {
    const allFurniture = this.app.getFurnitureManager()?.getAllFurniture() || [];
    const componentType = furnitureData?.componentType;
    const draggingUrlCode = furnitureData?.urlCode;
    let closest = null;
    let closestDist = SNAP_DISTANCE;
    let underCursor = null;
    let underCursorDistSq = PLUS_PICK_RADIUS_SQ;
    let underCursorRun = null;

    for (const furniture of allFurniture) {
      if (excludeFurniture?.has(furniture)) continue;
      if (!AttachmentPoint.hostTakes(furniture, furnitureData)) continue;

      for (const ap of furniture.attachmentPoints) {
        if (ap.isOccupied) continue;
        if (!AttachmentPoint.admits(ap.acceptsComponentTypes, ap.acceptsOnly, componentType, draggingUrlCode)) continue;

        // A ranged AP is as near as its nearest free stretch, and has a plus on each.
        if (ap.range) {
          if (!furniture.shouldShowAttachmentPoint(ap)) continue;
          const runs = this.app.getEventManager?.()?.componentFreeRuns(ap, furnitureData, excludeFurniture)
            ?? [freeRun(ap.range.min, ap.range.max)];
          furniture.getMesh().updateWorldMatrix(true, false);
          const matrixWorld = furniture.getMesh().matrixWorld;
          for (const run of runs) {
            ap.rangePoint(run.min, this._runStart).applyMatrix4(matrixWorld);
            ap.rangePoint(run.max, this._runEnd).applyMatrix4(matrixWorld);
            const dist = position.distanceTo(this._runLine.set(this._runStart, this._runEnd).closestPointToPoint(position, true, this._tempVec));
            if (dist < closestDist) {
              closestDist = dist;
              closest = ap;
            }
            if (!ray) continue;
            const rayDistSq = ray.distanceSqToPoint(ap.rangePoint(run.mid, this._tempVec).applyMatrix4(matrixWorld));
            if (rayDistSq < underCursorDistSq) {
              underCursorDistSq = rayDistSq;
              underCursor = ap;
              underCursorRun = run;
            }
          }
          continue;
        }

        const apPos = ap.getWorldPosition(this._scratchVec);
        const dist = position.distanceTo(apPos);
        const rayDistSq = ray ? ray.distanceSqToPoint(apPos) : Infinity;
        if (dist >= closestDist && rayDistSq >= underCursorDistSq) continue;
        if (!furniture.shouldShowAttachmentPoint(ap)) continue;
        if (dist < closestDist) {
          closestDist = dist;
          closest = ap;
        }
        if (rayDistSq < underCursorDistSq) {
          underCursorDistSq = rayDistSq;
          underCursor = ap;
          underCursorRun = null;
        }
      }
    }
    this.pickedRun = underCursorRun ? { ap: underCursor, t: underCursorRun.mid } : null;
    return underCursor ?? closest;
  }

  /**
   * Calculate the snapped world position and rotation for a dragged/placed module.
   *
   * Algorithm:
   * 1. For each child AP, compute the Y rotation needed for face-to-face alignment.
   * 2. Filter to candidates where the child's connecting face actually overlaps the
   *    parent's face (MIN_FACE_OVERLAP). This prevents diagonal corner-to-corner snaps
   *    where modules only touch at a point — while still allowing L-shapes where a
   *    wider module's face partially overlaps a narrower parent's face.
   * 3. Among valid candidates, rank by LANDING DISPLACEMENT — how far the piece's centre
   *    would travel from its live drag pose to the candidate's snapped pose (rotation
   *    baked in), breaking near-ties (within SELECTION_DIST_EPSILON) toward the
   *    less-rotated candidate so the choice stays stable at face boundaries.
   *
   * @param {import('../furniture/AttachmentPoint.js').AttachmentPoint} attachmentPoint
   * @param {Array} furnitureAttachmentPoints - The dragged furniture's AP definitions
   * @param {number} currentRotation - Current Y rotation of the furniture being placed
   * @param {THREE.Vector3|null} furniturePosition - Live drag pose for landing displacement
   * @param {number|null} [requiredRotation] - Only consider child anchors that land at this Y rotation
   * @returns {{position: THREE.Vector3, rotation: number, childAttachmentSide: string}|null}
   */
  calculateSnapPosition(attachmentPoint, furnitureAttachmentPoints, currentRotation, furniturePosition = null, requiredRotation = null) {
    const parentFurniture = attachmentPoint.parentFurniture;
    const parentMesh = parentFurniture ? parentFurniture.getMesh() : null;

    // Resolve parent's world quaternion
    this._parentWorldQuat.identity();
    if (parentMesh) parentMesh.getWorldQuaternion(this._parentWorldQuat);

    // Build parent attachment normal in world space.
    // normalAngle handles both cardinal sides and explicit facingAngle (curved
    // edges); normal = (sin θ, 0, cos θ) with θ = atan2(normalX, normalZ).
    const parentAngle = AttachmentPoint.normalAngle(attachmentPoint.side, attachmentPoint.facingAngle);
    this._parentLocalNormal.set(Math.sin(parentAngle), 0, Math.cos(parentAngle));
    const parentAttachmentNormal = this._parentNormalWorld.copy(this._parentLocalNormal).applyQuaternion(this._parentWorldQuat);
    const parentAttachmentWorldPos = attachmentPoint.getWorldPosition(this._parentAPWorld);
    const parentFaceKey = AttachmentPoint.faceKey(attachmentPoint.side, attachmentPoint.facingAngle);

    // Filter child APs to those compatible with this parent AP
    const allModuleAttachments = furnitureAttachmentPoints.filter(
      (ap) => ap.accepts?.includes("module") && this._canMatch(ap, attachmentPoint)
    );
    if (allModuleAttachments.length === 0) return null;

    // Target Y angle: the child AP must face opposite to parentNormal
    const targetAngle = Math.atan2(-parentAttachmentNormal.x, -parentAttachmentNormal.z);

    // Along-side axis: the axis parallel to the parent's face (perpendicular to
    // normal, in XZ plane). Used to measure face overlap between parent and
    // child connecting faces. Perp of (sin θ, cos θ) is (cos θ, -sin θ).
    this._alongSideAxisLocal.set(Math.cos(parentAngle), 0, -Math.sin(parentAngle));
    this._worldAlongSideAxis.copy(this._alongSideAxisLocal).applyQuaternion(this._parentWorldQuat);

    // Parent face span along the along-side axis (world space).
    // Computed from all module APs on the same side of the parent.
    let parentFaceMin = Infinity, parentFaceMax = -Infinity;
    const parentAPs = parentFurniture?.attachmentPoints || [];
    for (const pap of parentAPs) {
      if (AttachmentPoint.faceKey(pap.side, pap.facingAngle) !== parentFaceKey) continue;
      if (!pap.acceptsComponentTypes.includes("module")) continue;
      const papWorld = pap.getWorldPosition(this._faceAPWorld);
      const along = papWorld.dot(this._worldAlongSideAxis);
      if (along < parentFaceMin) parentFaceMin = along;
      if (along > parentFaceMax) parentFaceMax = along;
    }
    // Fallback if no module APs found on this side
    if (!isFinite(parentFaceMin)) {
      parentFaceMin = parentFaceMax = parentAttachmentWorldPos.dot(this._worldAlongSideAxis);
    }

    let bestResult = null;
    let bestRotDiff = Infinity;
    let bestLandDist = Infinity;

    allModuleAttachments.forEach((ap) => {
      // Local normal of this child AP in XZ plane (cardinal side or facingAngle)
      const childAngle = AttachmentPoint.normalAngle(ap.side, ap.facingAngle);
      const ncX = Math.sin(childAngle);
      const ncZ = Math.cos(childAngle);

      // Required Y rotation for this AP to oppose the parent normal
      const sourceAngle = Math.atan2(ncX, ncZ);
      let requiredRot = targetAngle - sourceAngle;
      while (requiredRot >  Math.PI) requiredRot -= 2 * Math.PI;
      while (requiredRot < -Math.PI) requiredRot += 2 * Math.PI;

      // Angular distance from current rotation
      let diff = requiredRot - currentRotation;
      while (diff >  Math.PI) diff -= 2 * Math.PI;
      while (diff < -Math.PI) diff += 2 * Math.PI;
      const rotDiff = Math.abs(diff);

      if (requiredRotation !== null) {
        let off = requiredRot - requiredRotation;
        while (off >  Math.PI) off -= 2 * Math.PI;
        while (off < -Math.PI) off += 2 * Math.PI;
        if (Math.abs(off) > ROTATION_MATCH_TOLERANCE) return;
      }

      // Candidate snap position
      this._rotationQuat.setFromAxisAngle(this._yAxis, requiredRot);
      this._candidateOffset.set(ap.offset.x, ap.offset.y, ap.offset.z)
        .applyQuaternion(this._rotationQuat);
      this._candidatePos.copy(parentAttachmentWorldPos).sub(this._candidateOffset);

      // --- Face-overlap check ---
      // Compute the child's connecting face span (all APs on the same side, rotated).
      // Reject candidates where the two connecting faces don't have positive area overlap
      // along the along-side axis. This prevents diagonal corner-to-corner connections
      // (e.g. k's back-right corner touching v's front-left corner with zero shared area).
      //
      // Special case: if either face is a single AP (span ≈ 0), relaxed rules apply:
      //   • single child AP → always valid (it lands exactly at the parent AP position,
      //     which is by definition within the parent face span)
      //   • single parent AP → valid if the parent AP position is contained within the
      //     child's face span
      // This handles collections like "choice" whose modules have one AP per side.
      const childFaceKey = AttachmentPoint.faceKey(ap.side, ap.facingAngle);
      const childSideAPs = furnitureAttachmentPoints.filter(
        f => f.accepts?.includes("module") && AttachmentPoint.faceKey(f.side, f.facingAngle) === childFaceKey
      );
      let childFaceMinRel = Infinity, childFaceMaxRel = -Infinity;
      for (const sap of childSideAPs) {
        this._tempVec.set(sap.offset.x, 0, sap.offset.z).applyQuaternion(this._rotationQuat);
        const rel = this._tempVec.dot(this._worldAlongSideAxis);
        if (rel < childFaceMinRel) childFaceMinRel = rel;
        if (rel > childFaceMaxRel) childFaceMaxRel = rel;
      }
      const candidateAlong = this._candidatePos.dot(this._worldAlongSideAxis);
      const childFaceMin = candidateAlong + childFaceMinRel;
      const childFaceMax = candidateAlong + childFaceMaxRel;
      const parentFaceSpan = parentFaceMax - parentFaceMin;
      const childFaceSpan = childFaceMaxRel - childFaceMinRel;
      const spanEps = 0.001;
      let faceValid;
      if (childFaceSpan < spanEps) {
        // Single child AP: it always coincides with the parent AP (within parent span). ✓
        faceValid = true;
      } else if (parentFaceSpan < spanEps) {
        // Single parent AP: check the parent AP is contained within the child face span.
        const parentAlong = parentAttachmentWorldPos.dot(this._worldAlongSideAxis);
        faceValid = childFaceMin <= parentAlong + spanEps && childFaceMax >= parentAlong - spanEps;
      } else {
        // Both faces are multi-AP spans: require actual area overlap.
        const faceOverlap = Math.min(parentFaceMax, childFaceMax) - Math.max(parentFaceMin, childFaceMin);
        faceValid = faceOverlap >= MIN_FACE_OVERLAP;
      }
      if (!faceValid) return;

      // Landing displacement: how far the dragged piece's CENTRE would travel from
      // its live cursor pose to this candidate's snapped pose (_candidatePos already
      // bakes in the fit rotation). This is exactly the metric
      // findClosestModuleAttachment ranks parent faces by, computed here per child
      // anchor so a single face can offer the orientation nearest the cursor.
      const landDist = furniturePosition ? this._candidatePos.distanceTo(furniturePosition) : 0;

      // Rank by LANDING DISPLACEMENT, not rotation. When one parent face accepts the
      // child through two different anchors (e.g. matacao3 corner: child right@112.5
      // needs 180° vs child front@-22.5 needs 45°), the two land at distinct poses
      // whose distances to the cursor cross over as it moves — so the face must offer
      // whichever lands nearest, letting a micro-move of the cursor flip between the
      // two snaps. Rotation-first selection could never reach the larger-rotation
      // orientation. Near-ties (within SELECTION_DIST_EPSILON) break toward the
      // smaller rotation for stability — and collapse the 180°-duplicate of a
      // symmetric straight face onto its less-rotated twin.
      const isBetter = landDist < bestLandDist - SELECTION_DIST_EPSILON ||
        (landDist < bestLandDist + SELECTION_DIST_EPSILON && rotDiff < bestRotDiff - 0.001);

      if (isBetter) {
        bestLandDist = landDist;
        bestRotDiff = rotDiff;
        bestResult = {
          position: this._candidatePos.clone(),
          rotation: requiredRot,
          childAttachmentSide: ap.side,
          // Expose the matched child AP's facingAngle so the penetration gate can
          // treat the snap as a curve connection when EITHER side's face is angled
          // (a curved child on a straight parent face still needs the oriented test).
          childFacingAngle: ap.facingAngle ?? null,
        };
      }
    });

    return bestResult;
  }
}
